import * as React from "react";
import { useIcon } from "@salt-ds/core";
import { StatusIndicator } from "../display/StatusIndicator";

import "./toast.css";

/**
 * Toast, ToastContent and Avatar — replacing `@salt-ds/core`'s behind `toast.tsx` and
 * `avatar.tsx`.
 *
 * ── A TOAST IS role="alert", AND THAT IS THE WHOLE COMPONENT ──────────────────────────
 *
 * `role="alert"` is an assertive live region: it interrupts whatever a screen reader is saying
 * and reads the toast immediately. Without it a toast is a div that appears in a corner and is
 * never announced at all — the message simply does not exist for anyone not looking at that
 * corner of the screen.
 *
 * Assertive is right HERE and wrong almost everywhere else. A toast is unsolicited and
 * short-lived: by the time a polite region got its turn, the toast may already have gone.
 */
export type ToastStatus = "info" | "error" | "warning" | "success";

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
  status?: ToastStatus;
  icon?: React.ReactNode;
}

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(function Toast(
  { children, className, icon, status, ...rest },
  ref,
) {
  const classes = ["saltToast"];
  if (status) classes.push(`saltToast-${status}`);
  if (className) classes.push(className);

  return (
    <div className={classes.join(" ")} role="alert" {...rest} ref={ref}>
      {/*
        The icon container exists only when there IS a status — an empty container would still
        occupy its grid column and indent the message for no reason.
      */}
      {status ? (
        <div className="saltToast-iconContainer">{icon ?? <StatusIndicator status={status} />}</div>
      ) : null}
      {children}
    </div>
  );
});

export type ToastContentProps = React.HTMLAttributes<HTMLDivElement>;

export const ToastContent = React.forwardRef<HTMLDivElement, ToastContentProps>(
  function ToastContent({ children, className, ...rest }, ref) {
    return (
      <div
        className={className ? `saltToastContent ${className}` : "saltToastContent"}
        {...rest}
        ref={ref}
      >
        {children}
      </div>
    );
  },
);

type ImageStatus = "pending" | "loading" | "loaded" | "error";

function resolveStatus(image: HTMLImageElement | null, src?: string): ImageStatus {
  if (!image) return "pending";
  if (!src) return "error";
  if (image.src !== src) image.src = src;
  // `complete` alone is not enough: a FAILED load is also "complete". `naturalWidth > 0` is what
  // separates an image that arrived from one that 404'd, and without it a broken URL renders as
  // a broken-image glyph instead of falling back to initials.
  return image.complete && image.naturalWidth > 0 ? "loaded" : "loading";
}

/**
 * Tracks whether an avatar's image actually arrived.
 *
 * It loads through a detached `new Image()` rather than by rendering an `<img>` and waiting.
 * That is what makes the fallback seamless: the DOM only ever shows the image once it has
 * loaded, so a failed URL never flashes a broken-image icon on its way to the initials.
 */
function useAvatarImage(src?: string): ImageStatus {
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const image = (() => {
    if (typeof window === "undefined") return null;
    if (!imageRef.current) imageRef.current = new window.Image();
    return imageRef.current;
  })();

  const [status, setStatus] = React.useState<ImageStatus>(() => resolveStatus(image, src));

  React.useLayoutEffect(() => {
    setStatus(resolveStatus(image, src));
  }, [image, src]);

  React.useLayoutEffect(() => {
    if (!image) return;
    const handleLoad = () => setStatus("loaded");
    const handleError = () => setStatus("error");
    image.addEventListener("load", handleLoad);
    image.addEventListener("error", handleError);
    return () => {
      image.removeEventListener("load", handleLoad);
      image.removeEventListener("error", handleError);
    };
  }, [image]);

  return status;
}

export type AvatarColor = "accent" | "category-1" | "category-2" | "category-3";

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  color?: AvatarColor | string;
  name?: string;
  nameToInitials?: (name?: string) => string | undefined;
  src?: string;
  size?: number;
  fallbackIcon?: React.ReactNode;
}

const DEFAULT_AVATAR_SIZE = 2;

/** First letters of the first two words — "Aaron Smith" becomes "AS". */
const defaultNameToInitials = (name?: string) =>
  name
    ?.split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(function Avatar(
  {
    children: childrenProp,
    className,
    color = "accent",
    fallbackIcon: fallbackIconProp,
    name,
    nameToInitials = defaultNameToInitials,
    size = DEFAULT_AVATAR_SIZE,
    src,
    style: styleProp,
    ...rest
  },
  ref,
) {
  const { UserIcon } = useIcon();
  const fallbackIcon = fallbackIconProp === undefined ? <UserIcon aria-hidden /> : fallbackIconProp;

  const status = useAvatarImage(src);
  const hasImage = status === "loaded";

  let children: React.ReactNode;
  if (hasImage) {
    // `alt=""` deliberately: the AVATAR carries the name via `aria-label`, so a described image
    // inside it would announce the person twice.
    children = <img alt="" src={src} />;
  } else if (childrenProp != null) {
    children = childrenProp;
  }

  const classes = ["saltAvatar", `saltAvatar-${color}`];
  if (hasImage) classes.push("saltAvatar-withImage");
  if (className) classes.push(className);

  /*
   * `role="img"` with a label ONLY when there is a name. An avatar showing a generic person icon
   * and nothing else is decoration, and labelling it "image" tells a screen-reader user there is
   * something to look at when there is not.
   */
  const ariaProps = name ? { role: "img", "aria-label": name } : {};

  return (
    <div
      className={classes.join(" ")}
      ref={ref}
      style={{ ...styleProp, "--saltAvatar-size-multiplier": `${size}` } as React.CSSProperties}
      {...ariaProps}
      {...rest}
    >
      {/* Image, then explicit children, then initials, then the icon — each step is a real
          fallback, not a default: a name with no image still identifies the person. */}
      {children || nameToInitials(name) || fallbackIcon}
    </div>
  );
});
